import _ from 'lodash'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Button, Card, Container, Divider, Paper, Stack, Tab, Tabs, Typography } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import PublicIcon from '@mui/icons-material/Public'

import NetworkCoverPhoto from '@/pages/network/components/NetworkCoverPhoto'
import PageContainer from '@/components/ui/containers/PageContainer'

type NetworkPreviewProps = {
  description: string
  name: string
  privacy: 'public' | 'private'
}

export default function NetworkPreview(props: NetworkPreviewProps) {
  const { privacy, name, description } = props
  const { t } = useTranslation()

  const tabs = useMemo(
    () => [
      { id: 'about', label: t('page.network.tab.about.label') },
      { id: 'members', label: t('page.network.tab.members.label') },
      {
        id: 'consultationOrders',
        label: t('page.network.tab.consultationOrders.label'),
      },
      { id: 'expertiseOrders', label: t('page.network.tab.expertiseOrders.label') },
    ],
    [t]
  )

  return (
    <Card>
      <Stack direction="column">
        <Stack mx={2}>
          <Typography variant="caption" pt={3} fontSize={16} fontWeight={700}>
            {t('page.createNetwork.networkPreview.previewMode.title')}
          </Typography>
        </Stack>
        <Box flex={1} m={2} border="1px solid rgba(0,0,0,0.1)" borderRadius={2}>
          <NetworkCoverPhoto disabled />
          <Container maxWidth="xl" sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Stack spacing={1} sx={{ pl: '30px', pr: '30px' }}>
              <Stack>
                <Typography variant="h2" sx={{ color: (theme) => theme.palette.text.disabled, mt: '30px' }}>
                  {name || t('page.createNetwork.networkPreview.title')}
                </Typography>
                <Stack direction="row" alignItems="center" justifyContent="space-between" py={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {privacy === 'public' && <PublicIcon fontSize="small" sx={{ color: (theme) => theme.palette.grey[500] }} />}
                    {privacy === 'private' && <LockOpenIcon fontSize="small" sx={{ color: (theme) => theme.palette.grey[500] }} />}
                    <Box sx={{ color: (theme) => theme.palette.grey[600], fontSize: '15px', fontWeight: 400 }}>
                      {t(`page.network.privacy.${privacy}.primary`)}
                    </Box>
                  </Stack>
                  <Stack direction="row">
                    <Button color="primary" disabled startIcon={<AddRoundedIcon />} variant="contained">
                      {t('common:button.invite.label')}
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
              <Stack>
                <Divider />
                <Stack direction="row" alignItems="center">
                  <Box flex={1}>
                    <Tabs value={0}>
                      {_.map(tabs, (tab) => (
                        <Tab key={tab.id} disabled label={tab.label} />
                      ))}
                    </Tabs>
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          </Container>
          <PageContainer sx={{ minHeight: '500px' }}>
            <Paper>
              <Box sx={{ px: { xs: 2, sm: 3 }, py: '20px' }}>
                <Typography component="h2" sx={{ color: (theme) => theme.palette.grey[900], opacity: 0.8, fontSize: '18px', lineHeight: '24px' }}>
                  {t('page.network.about.title')}
                </Typography>
              </Box>
              <Divider />
              <Box
                className="ql-editor"
                dangerouslySetInnerHTML={{ __html: description }}
                sx={{
                  color: (theme) => theme.palette.grey[600],
                  fontSize: '15px',
                  lineHeight: '24px',
                  p: '10px',
                  '& img': {
                    objectFit: 'contain',
                    maxWidth: '100%',
                    height: 'auto',
                    opacity: '0.8',
                  },
                }}
              />
            </Paper>
          </PageContainer>
        </Box>
      </Stack>
    </Card>
  )
}
